import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PushPin, X, MagnifyingGlass, MapPin } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Sweeper Radar: tap open WOs to pin them to one truck for `date`.
export default function SweeperWoPicker({ truckKey, truckLabel, date, pinnedIds, onChange, onClose }) {
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load OPEN work orders. Bump the limit high and pull only the statuses
  // we can actually pin — otherwise a big tenant's 500 completed jobs push
  // the WO the dispatcher is looking for off the end of the list, which
  // is exactly the "sweeper can't find my WO" complaint we're fixing.
  useEffect(() => {
    let alive = true;
    const term = q.trim();
    setLoading(true);
    // Debounce server search — 250 ms so we don't hammer while typing.
    const t = setTimeout(() => {
      const params = new URLSearchParams({ limit: "1000" });
      if (term) params.set("q", term);
      api.get(`/jobs?${params.toString()}`)
        .then(r => {
          if (!alive) return;
          setJobs((r.data || []).filter(j =>
            ["pool", "assigned", "new", "unassigned", "follow_up"].includes(j.status)
          ));
        })
        .catch(() => toast.error("Could not load WOs"))
        .finally(() => { if (alive) setLoading(false); });
    }, term ? 250 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const list = useMemo(() => {
    // Server already filtered by search term — this is just the visible slice.
    return jobs.slice(0, 200);
  }, [jobs]);

  const toggle = async (j) => {
    const isPinnedHere = pinnedIds.has(j.id);
    setBusy(j.id);
    try {
      if (isPinnedHere) await api.post("/jobs/sweeper-unpin", { job_ids: [j.id] });
      else await api.post("/jobs/sweeper-pin", { job_ids: [j.id], truck_key: truckKey, truck_label: truckLabel, date });
      onChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not pin")); }
    finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="wo-picker-overlay">
      <div className="bg-white w-full sm:max-w-lg max-h-[88vh] flex flex-col border-2 border-slate-900 shadow-[6px_6px_0_#0f172a]" onClick={e => e.stopPropagation()} data-testid="wo-picker">
        <div className="p-3 border-b-2 border-slate-900 flex items-center gap-2">
          <PushPin weight="fill" size={18} className="text-amber-500"/>
          <div className="min-w-0">
            <div className="text-xs font-black uppercase tracking-widest truncate">Pin WOs → {truckLabel}</div>
            <div className="text-[10px] text-slate-500 font-bold">{date} · tap to add, tap again to remove</div>
          </div>
          <button onClick={onClose} className="ml-auto h-9 w-9 flex items-center justify-center hover:bg-slate-100" data-testid="wo-picker-close"><X weight="bold" size={18}/></button>
        </div>
        <div className="p-2 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
          <MagnifyingGlass size={16} className="text-slate-400"/>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="WO#, address, unit, title…" data-testid="wo-picker-search"
            className="flex-1 h-10 px-2 border-2 border-slate-300 text-sm font-medium bg-white"/>
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
          {list.map(j => {
            const here = pinnedIds.has(j.id);
            const elsewhere = !here && j.sweeper_pin && j.sweeper_pin.date === date;
            return (
              <button key={j.id} onClick={() => toggle(j)} disabled={busy === j.id} data-testid={`wo-pick-${j.id}`}
                className={`w-full text-left p-3 flex items-start gap-3 active:bg-amber-100 ${here ? "bg-amber-50" : "bg-white"}`}>
                <span className={`h-7 w-7 flex-shrink-0 border-2 flex items-center justify-center text-sm font-black ${here ? "bg-amber-400 border-slate-900" : "border-slate-300 text-transparent"}`}>📌</span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-1.5 flex-wrap">
                    {j.work_order_number && <span className="font-mono text-[10px] bg-[#0f172a] text-white px-1">WO#{j.work_order_number}</span>}
                    {j.portfolio_name && <span className="text-[9px] text-white px-1 font-black uppercase" style={{ background: j.portfolio_color || "#0f172a" }}>{j.portfolio_name}</span>}
                    {elsewhere && <span className="text-[9px] bg-slate-200 text-slate-700 px-1 font-black uppercase">📌 {j.sweeper_pin.truck_label}</span>}
                    {j.return_visit?.date && <span className="text-[9px] bg-orange-500 text-white px-1 font-black uppercase">↩ {j.return_visit.date}</span>}
                  </span>
                  <span className="block text-sm font-bold text-slate-900 truncate">{j.title}</span>
                  <span className="block text-[11px] text-slate-500 truncate inline-flex items-center gap-1"><MapPin size={10}/>{j.property_address}{j.unit ? ` · #${j.unit}` : ""}</span>
                </span>
              </button>
            );
          })}
          {list.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500 font-bold">
              {loading ? "Searching…" : q.trim() ? `No open WOs match "${q.trim()}"` : "No open WOs"}
            </div>
          )}
        </div>
        <div className="p-2 border-t-2 border-slate-900 flex items-center justify-between">
          <span className="text-[11px] font-black uppercase tracking-widest text-slate-600" data-testid="wo-picker-count">
            {pinnedIds.size} pinned · {list.length} shown
          </span>
          <button onClick={onClose} className="h-10 px-4 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest" data-testid="wo-picker-done">Done</button>
        </div>
      </div>
    </div>
  );
}
